import { useCallback, useEffect, useState } from "react";
import MediaUploader from "../common/media-uploader";
import { useFormatCurrency } from "../common/format-currency";
const PaymentMethod = ({
  cart,
  onProceed,
  accessPoint,
  paymentMethod,
  gotoCart,
  setPaymentMethod,
  disableStoreProductPrice,
}) => {
  const { formatCurrency } = useFormatCurrency();
  const [error, setError] = useState("");
  const [uploadedMedia, setUploadedMedia] = useState([]);

  const [isUploading, setIsUploading] = useState(false);

  const noPaymentNote =
    "Please note that your order is not reserved for you without payment. If you want it to be reserved, click on the other payment methods to pay immediately. If not, please click the button below to proceed.";

  const handleMediaUploadSuccess = (mediaData) => {
    setError("");
    setUploadedMedia(mediaData);
  };

  const handleChange = useCallback((e) => {
    setError("");
    setPaymentMethod(e.target.value);
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!paymentMethod) {
        setError("Please select a payment method.");
        return;
      }

      if (paymentMethod === "BANK_TRANSFER") {
        if (uploadedMedia.length === 0) {
          setError("Please upload your receipt before submitting.");
          return;
        }
        onProceed(paymentMethod, uploadedMedia);
      } else {
        onProceed(paymentMethod);
      }

      setError("");
    },
    [paymentMethod, uploadedMedia, onProceed]
  );

  useEffect(() => {
    // Negotiable pricing always overrides
    if (disableStoreProductPrice) {
      if (paymentMethod !== "NO_PAYMENT") {
        setPaymentMethod("NO_PAYMENT");
      }
      return;
    }

    // Build allowed options dynamically
    const allowedMethods = [];
    if (!disableStoreProductPrice) {
      allowedMethods.push("ONLINE");
      if (
        accessPoint?.settings?.enableStoreOfflinePayment &&
        accessPoint?.invoiceBankAccount?.[0]
      ) {
        allowedMethods.push("BANK_TRANSFER");
      }
    }
    if (accessPoint?.settings?.enableOrderPlacementWithoutPayment) {
      allowedMethods.push("NO_PAYMENT");
    }

    // If current method is invalid, reset to default
    if (!allowedMethods.includes(paymentMethod)) {
      if (allowedMethods.includes("ONLINE")) {
        setPaymentMethod("ONLINE");
      } else if (allowedMethods.includes("BANK_TRANSFER")) {
        setPaymentMethod("BANK_TRANSFER");
      } else if (allowedMethods.includes("NO_PAYMENT")) {
        setPaymentMethod("NO_PAYMENT");
      } else {
        setPaymentMethod(""); // no valid options at all (shouldn’t happen)
      }
    }
  }, [paymentMethod, disableStoreProductPrice, accessPoint]);

  return (
    <>
      <div className="auth-form-container1">
        <h3>Payment Method</h3>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="radio-box">
            {!disableStoreProductPrice && (
              <>
                <div className="radio-input">
                  <label htmlFor="online">
                    <input
                      type="radio"
                      id="online"
                      value="ONLINE"
                      name="paymentMethod"
                      checked={paymentMethod === "ONLINE"}
                      onChange={handleChange}
                    />
                    Pay with Paystack
                  </label>
                </div>

                {accessPoint?.settings?.enableStoreOfflinePayment &&
                  accessPoint?.invoiceBankAccount?.[0] && (
                    <div className="radio-input">
                      <label htmlFor="bank-transfer">
                        <input
                          type="radio"
                          id="bank-transfer"
                          value="BANK_TRANSFER"
                          name="paymentMethod"
                          checked={paymentMethod === "BANK_TRANSFER"}
                          onChange={handleChange}
                        />
                        Offline Bank Transfer
                      </label>
                    </div>
                  )}

                {paymentMethod === "BANK_TRANSFER" && (
                  <div className="payment-container">
                    <div className="payment-box">
                      <div className="payment-text">
                        Please transfer{" "}
                        <strong>
                          {formatCurrency(cart.totalAmount, cart.currency)}
                        </strong>{" "}
                        to the bank account below, and upload your proof of
                        payment to proceed.
                      </div>
                      {accessPoint?.invoiceBankAccount?.[0] && (
                        <div className="payment-info">
                          <h1>{accessPoint.invoiceBankAccount[0].accountNo}</h1>
                          <h2>{accessPoint.invoiceBankAccount[0].bankName}</h2>
                          <p>{accessPoint.invoiceBankAccount[0].accountName}</p>
                        </div>
                      )}
                      <MediaUploader
                        onSuccess={handleMediaUploadSuccess}
                        setError={setError}
                        setIsUploading={setIsUploading}
                        // supportedFiles={["image/*", "application/pdf"]}
                        supportedFiles={["image/*,video/*,application/pdf"]}
                        allowMultipleUpload={true}
                        destination={"attachments"}
                        uploadText={"Upload Payment Receipt"}
                        presetDocuments={uploadedMedia}
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            {(accessPoint?.settings?.enableOrderPlacementWithoutPayment ||
              disableStoreProductPrice) && (
              <div className="radio-input">
                <label htmlFor="no-payment">
                  <input
                    type="radio"
                    id="no-payment"
                    value="NO_PAYMENT"
                    name="paymentMethod"
                    checked={paymentMethod === "NO_PAYMENT"}
                    onChange={handleChange}
                  />
                  Place order without payment
                </label>
                {paymentMethod === "NO_PAYMENT" &&
                  !disableStoreProductPrice && <span>{noPaymentNote}</span>}
              </div>
            )}
          </div>
          <div className="login-container">
            <button
              type="submit"
              className="auth-submit-button"
              aria-label="Submit delivery method"
              disabled={isUploading || !paymentMethod}
            >
              {isUploading ? "Document Uploading, Waiting..." : "Proceed"}
            </button>
            <button
              className="checkout-secondary-button full"
              onClick={(e) => {
                e.preventDefault();
                gotoCart();
              }}
            >
              Go Back to Cart
            </button>
          </div>
        </form>
      </div>
    </>
  );
};
export default PaymentMethod;
